/**
 * Logo Overlay Modal
 *
 * Allows Super-Admins to composite the branding logo onto a video background.
 * All compositing happens client-side via the Canvas API.
 */
import { useState, useRef, useEffect, useCallback } from "react";
import { useMutation } from "convex/react";
import { api } from "@jourfx/convex/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  Button,
  Label,
  useToast,
} from "@jourfx/ui";
import { Loader2 } from "lucide-react";

type Corner = "top-left" | "top-right" | "bottom-left" | "bottom-right";

interface LogoOverlayModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  backgroundId: string;
  /** URL of the original (non-composited) background image */
  imageUrl: string;
  /** Pre-existing overlay settings (for re-editing) */
  existingOverlay?: {
    corner: Corner;
    sizePct: number;
    opacityPct: number;
  } | null;
}

const CORNERS: { value: Corner; label: string }[] = [
  { value: "top-left", label: "Oben links" },
  { value: "top-right", label: "Oben rechts" },
  { value: "bottom-left", label: "Unten links" },
  { value: "bottom-right", label: "Unten rechts" },
];

const PADDING_RATIO = 0.03;

function getConvexBaseUrl() {
  return (
    import.meta.env.VITE_CONVEX_SITE_URL ||
    import.meta.env.VITE_CONVEX_URL ||
    "http://localhost:3211"
  );
}

function getLogoUrl() {
  const base = getConvexBaseUrl().replace(/\/$/, "");
  return `${base}/api/branding/logo-primary?v=${Date.now()}`;
}

function getSquareLogoUrl() {
  const base = getConvexBaseUrl().replace(/\/$/, "");
  return `${base}/api/branding/logo-square?v=${Date.now()}`;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
}

export function LogoOverlayModal({
  open,
  onOpenChange,
  backgroundId,
  imageUrl,
  existingOverlay,
}: LogoOverlayModalProps) {
  const { toast } = useToast();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const bgImageRef = useRef<HTMLImageElement | null>(null);
  const logoImageRef = useRef<HTMLImageElement | null>(null);

  const [corner, setCorner] = useState<Corner>(
    existingOverlay?.corner ?? "bottom-right"
  );
  const [sizePct, setSizePct] = useState(existingOverlay?.sizePct ?? 15);
  const [opacityPct, setOpacityPct] = useState(
    existingOverlay?.opacityPct ?? 80
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isApplying, setIsApplying] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const generateUploadUrl = useMutation(
    api.admin.videoBackgrounds.generateUploadUrl
  );
  const updateWithOverlay = useMutation(
    api.admin.videoBackgrounds.updateWithOverlay
  );

  // Load background and logo images when modal opens
  useEffect(() => {
    if (!open) return;

    setIsLoading(true);
    setLoadError(null);

    // Reset to existing overlay settings or defaults when opening
    setCorner(existingOverlay?.corner ?? "bottom-right");
    setSizePct(existingOverlay?.sizePct ?? 15);
    setOpacityPct(existingOverlay?.opacityPct ?? 80);

    async function loadImages() {
      try {
        const bgImg = await loadImage(imageUrl);
        bgImageRef.current = bgImg;

        // Try primary logo first, fall back to square
        let logoImg: HTMLImageElement;
        try {
          logoImg = await loadImage(getLogoUrl());
        } catch {
          logoImg = await loadImage(getSquareLogoUrl());
        }
        logoImageRef.current = logoImg;

        setIsLoading(false);
      } catch (err) {
        setLoadError(
          err instanceof Error ? err.message : "Bilder konnten nicht geladen werden"
        );
        setIsLoading(false);
      }
    }

    loadImages();
  }, [open, imageUrl, existingOverlay]);

  // Draw preview whenever settings change
  const drawPreview = useCallback(() => {
    const canvas = canvasRef.current;
    const bgImg = bgImageRef.current;
    const logoImg = logoImageRef.current;
    if (!canvas || !bgImg || !logoImg) return;

    // Preview at a reasonable display size while maintaining aspect ratio
    const maxWidth = 640;
    const scale = Math.min(1, maxWidth / bgImg.naturalWidth);
    canvas.width = bgImg.naturalWidth * scale;
    canvas.height = bgImg.naturalHeight * scale;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Draw background
    ctx.drawImage(bgImg, 0, 0, canvas.width, canvas.height);

    // Draw logo
    const logoWidth = canvas.width * (sizePct / 100);
    const logoHeight =
      logoWidth * (logoImg.naturalHeight / logoImg.naturalWidth);
    const padding = canvas.width * PADDING_RATIO;

    let x: number;
    let y: number;
    switch (corner) {
      case "top-left":
        x = padding;
        y = padding;
        break;
      case "top-right":
        x = canvas.width - logoWidth - padding;
        y = padding;
        break;
      case "bottom-left":
        x = padding;
        y = canvas.height - logoHeight - padding;
        break;
      case "bottom-right":
        x = canvas.width - logoWidth - padding;
        y = canvas.height - logoHeight - padding;
        break;
    }

    ctx.globalAlpha = opacityPct / 100;
    ctx.drawImage(logoImg, x, y, logoWidth, logoHeight);
    ctx.globalAlpha = 1;
  }, [corner, sizePct, opacityPct]);

  useEffect(() => {
    if (!isLoading && !loadError) {
      drawPreview();
    }
  }, [isLoading, loadError, drawPreview]);

  const handleApply = async () => {
    const bgImg = bgImageRef.current;
    const logoImg = logoImageRef.current;
    if (!bgImg || !logoImg) return;

    setIsApplying(true);
    try {
      // Composite at full resolution
      const offscreen = document.createElement("canvas");
      offscreen.width = bgImg.naturalWidth;
      offscreen.height = bgImg.naturalHeight;
      const ctx = offscreen.getContext("2d");
      if (!ctx) throw new Error("Canvas context not available");

      ctx.drawImage(bgImg, 0, 0);

      const logoWidth = offscreen.width * (sizePct / 100);
      const logoHeight =
        logoWidth * (logoImg.naturalHeight / logoImg.naturalWidth);
      const padding = offscreen.width * PADDING_RATIO;

      let x: number;
      let y: number;
      switch (corner) {
        case "top-left":
          x = padding;
          y = padding;
          break;
        case "top-right":
          x = offscreen.width - logoWidth - padding;
          y = padding;
          break;
        case "bottom-left":
          x = padding;
          y = offscreen.height - logoHeight - padding;
          break;
        case "bottom-right":
          x = offscreen.width - logoWidth - padding;
          y = offscreen.height - logoHeight - padding;
          break;
      }

      ctx.globalAlpha = opacityPct / 100;
      ctx.drawImage(logoImg, x, y, logoWidth, logoHeight);
      ctx.globalAlpha = 1;

      // Export as PNG blob
      const blob = await new Promise<Blob>((resolve, reject) => {
        offscreen.toBlob(
          (b) => (b ? resolve(b) : reject(new Error("Canvas export failed"))),
          "image/png"
        );
      });

      // Upload to Convex
      const uploadUrl = await generateUploadUrl({});
      const res = await fetch(uploadUrl, {
        method: "POST",
        headers: { "Content-Type": "image/png" },
        body: blob,
      });
      if (!res.ok) throw new Error(`Upload fehlgeschlagen (Status ${res.status})`);
      const json = (await res.json()) as { storageId?: string };
      if (!json.storageId) throw new Error("Keine Storage-ID erhalten");

      // Update background with overlay
      await updateWithOverlay({
        id: backgroundId as any,
        compositedStorageId: json.storageId as any,
        corner,
        sizePct,
        opacityPct,
      });

      toast({
        title: "Logo-Overlay angewendet",
        description: "Das Logo wurde auf das Hintergrundbild angewendet.",
      });
      onOpenChange(false);
    } catch (err) {
      toast({
        title: "Fehler",
        description:
          err instanceof Error ? err.message : "Unbekannter Fehler",
        variant: "destructive",
      });
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Logo-Overlay hinzufügen</DialogTitle>
          <DialogDescription>
            Platzieren Sie das Branding-Logo auf dem Hintergrundbild.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Preview */}
          <div className="rounded-lg border bg-muted/50 p-2 flex items-center justify-center">
            {isLoading ? (
              <div className="flex items-center justify-center h-48">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : loadError ? (
              <div className="text-sm text-destructive p-4">{loadError}</div>
            ) : (
              <canvas
                ref={canvasRef}
                className="max-w-full h-auto rounded"
              />
            )}
          </div>

          {/* Corner picker */}
          <div className="space-y-2">
            <Label>Position</Label>
            <div className="grid grid-cols-2 gap-2 max-w-xs">
              {CORNERS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setCorner(c.value)}
                  className={`px-3 py-2 text-sm rounded-md border transition-colors ${
                    corner === c.value
                      ? "border-primary bg-primary/10 text-primary font-medium"
                      : "border-border bg-background text-muted-foreground hover:bg-muted"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          {/* Size slider */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Größe</Label>
              <span className="text-sm text-muted-foreground">{sizePct}%</span>
            </div>
            <input
              type="range"
              min={5}
              max={30}
              step={1}
              value={sizePct}
              onChange={(e) => setSizePct(Number(e.target.value))}
              className="w-full accent-primary"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Klein</span>
              <span>Groß</span>
            </div>
          </div>

          {/* Opacity slider */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Deckkraft</Label>
              <span className="text-sm text-muted-foreground">
                {opacityPct}%
              </span>
            </div>
            <input
              type="range"
              min={20}
              max={100}
              step={5}
              value={opacityPct}
              onChange={(e) => setOpacityPct(Number(e.target.value))}
              className="w-full accent-primary"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Dezent</span>
              <span>Voll</span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isApplying}
          >
            Abbrechen
          </Button>
          <Button
            onClick={handleApply}
            disabled={isLoading || !!loadError || isApplying}
          >
            {isApplying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Anwenden
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
